import React from 'react';
import './Experience.css';

const roles = [
  {
    title: 'Data Engineer',
    org: 'DevCo Residential Group',
    dates: 'Jun 2026 – Present',
    summary: "Building the company's data platform from the ground up.",
    bullets: [
      'Built Python API extractors that bring HR, property-management, and operations data into Snowflake.',
      'Set up Snowflake security and governance, including role-based access control, warehouse and schema structure, and data-access policies.',
      'Built layered raw, staging, and mart models in dbt to standardize business logic and reporting metrics.',
      'Configured an Azure VM with a static, whitelisted IP to route extractor traffic into Snowflake for source systems that require IP allowlisting.',
      'Orchestrated pipelines with GitHub Actions and scheduled jobs with structured logging.',
      'Delivered Power BI reporting on shared semantic models with row-level security and scheduled refreshes.',
      'Used Snowflake CoCo for data discovery and governed documentation of curated models.',
    ],
  },
  {
    title: 'Inventory Planner (Contract)',
    org: 'Amazon Leo, Everett, WA',
    dates: 'Jul 2025 – Jun 2026',
    bullets: [
      'Reconciled SAP, Smartsheet, ERP, and physical-inventory records to resolve discrepancies and maintain forecast-ready data.',
      'Built Python and Excel analyses to identify storage inefficiencies and rank inventory-consolidation opportunities using batch, aisle, goods-receipt date, handling-unit, and bin attributes.',
      'Analyzed demand, lead-time, and inventory-availability signals to surface supply constraints and prioritize operational follow-up.',
    ],
  },
  {
    title: 'Data Analyst',
    org: 'Next Level AMZ, Port Orchard, WA',
    dates: 'Apr 2024 – Jul 2025',
    bullets: [
      'Built Python and SQL pipelines on Amazon Selling Partner API data to unify sales, traffic, fulfillment, inventory, and operational metrics, reducing manual reporting work by 60%.',
      'Developed Power BI and Microsoft Fabric dashboards that gave leadership visibility into fulfillment, inventory health, advertising performance, and conversion trends.',
      'Consolidated fragmented Excel, PostgreSQL, and API-platform data into a unified reporting layer used in weekly advertising and sales reviews.',
    ],
  },
];

const education = {
  school: 'Washington State University',
  degree: 'B.S. Software Engineering',
  dates: 'Apr 2024',
};

const skillGroups = [
  {
    title: 'Warehouse & Transformation',
    skills: ['Snowflake', 'dbt', 'SQL', 'ELT', 'Dimensional Modeling'],
  },
  {
    title: 'Ingestion & Orchestration',
    skills: ['Python', 'REST APIs', 'GitHub Actions', 'Scheduled Jobs & Logging'],
  },
  {
    title: 'BI & Reporting',
    skills: ['Power BI', 'Microsoft Fabric', 'Semantic Models', 'Row-Level Security', 'PostgreSQL'],
  },
  {
    title: 'Cloud, Quality & AI',
    skills: ['Azure VM (Static IP)', 'Git', 'Data Validation', 'Claude Code', 'Snowflake CoCo'],
  },
];

const Experience = () => {
  return (
    <section className="experience-page">
      <div className="experience-content">
        <h1>Experience</h1>
        <p className="experience-subtitle">
          Building data platforms and analytics systems across warehouse operations, ecommerce, and
          residential property management.
        </p>

        <ol className="timeline">
          {roles.map((role) => (
            <li className="timeline-item" key={`${role.title}-${role.org}`}>
              <div className="timeline-marker" aria-hidden="true" />
              <div className="timeline-card">
                <div className="timeline-card-header">
                  <div>
                    <h2 className="timeline-title">{role.title}</h2>
                    <p className="timeline-org">{role.org}</p>
                  </div>
                  <span className="timeline-dates">{role.dates}</span>
                </div>
                {role.summary && <p className="timeline-summary">{role.summary}</p>}
                <ul className="timeline-bullets">
                  {role.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            </li>
          ))}

          <li className="timeline-item">
            <div className="timeline-marker" aria-hidden="true" />
            <div className="timeline-card">
              <div className="timeline-card-header">
                <div>
                  <h2 className="timeline-title">{education.degree}</h2>
                  <p className="timeline-org">{education.school}</p>
                </div>
                <span className="timeline-dates">{education.dates}</span>
              </div>
            </div>
          </li>
        </ol>

        <h2 className="skills-heading">Skills</h2>
        <div className="skills-grid">
          {skillGroups.map((group) => (
            <div className="skills-group" key={group.title}>
              <h3>{group.title}</h3>
              <div className="skills-pill-row">
                {group.skills.map((skill) => (
                  <span className="skills-pill" key={skill}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
